/**
 * Autotraderweb (binarysystemdev) API JWT — authblocks anonymous gate + optional site login.
 * Independent of Deriv v4 OAuth (`tradingbots.v4.*`). Separate keys/headers avoid attaching the wrong token.
 *
 * - CORS: browser must be allowed to POST/GET to the autotrader base URL; failures should surface (no silent undefined tokens).
 * - Expected JSON shape: `{ token, ... }` (not `{ data: { token } }`); validate centrally here.
 * - XSS: tokens use localStorage like the rest of the app; prefer CSP. Persist minimal `user` subset only (see loginAutotraderSite).
 * - Multi-tab: `storage` listener in View reloads when `AUTOTRADER_STORAGE` keys change from another tab.
 * - Base URL: hostname heuristic (localhost / 127.0.0.1 / 192.168.* → :9000). For staging, consider build-time override.
 */

import { get, set, remove } from './utils/storageManager';
import { supabase } from './supabaseClient';

export const AUTOTRADER_STORAGE = {
    SESSION_TOKEN: 'tradingbots.autotrader.sessionToken',
    USER_SNAPSHOT: 'tradingbots.autotrader.user',
    SITE_LOGGED  : 'tradingbots.autotrader.siteLogged',
};

const ANONYMOUS_CACHE_MS = 120000;
let anonymousJwtCache = { token: null, fetchedAt: 0 };

export class AutotraderAuthError extends Error {
    constructor(message, status = 0) {
        super(message);
        this.name = 'AutotraderAuthError';
        this.status = status;
    }
}

export function getAutotraderBaseUrl() {
    const host = typeof window !== 'undefined' && window.location && window.location.hostname;
    const isLocal =
        host === 'localhost' || host === '127.0.0.1' || (typeof host === 'string' && host.startsWith('192.168.'));
    return isLocal ? 'http://localhost:9000' : 'https://autotraderweb.app';
}

function parseTokenFromResponse(data) {
    if (!data || typeof data !== 'object') {
        throw new AutotraderAuthError('Invalid auth response');
    }
    const { token } = data;
    if (!token || typeof token !== 'string') {
        throw new AutotraderAuthError('Missing token in auth response');
    }
    return token;
}

function jwtExpMs(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }
        let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const pad = base64.length % 4;
        if (pad) {
            base64 += '='.repeat(4 - pad);
        }
        const payload = JSON.parse(atob(base64));
        if (typeof payload.exp === 'number') {
            return payload.exp * 1000;
        }
    } catch (e) {
        // ignore
    }
    return null;
}

function isStoredSessionTokenValid(token) {
    if (!token || typeof token !== 'string') {
        return false;
    }
    const expMs = jwtExpMs(token);
    if (expMs === null) {
        return true;
    }
    return Date.now() < expMs - 60000;
}

export function getStoredAutotraderSiteUser() {
    const raw = get(AUTOTRADER_STORAGE.USER_SNAPSHOT);
    if (!raw) {
        return null;
    }
    try {
        return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (e) {
        return null;
    }
}

export function isAutotraderSiteLoggedIn() {
    return get(AUTOTRADER_STORAGE.SITE_LOGGED) === '1' && !!get(AUTOTRADER_STORAGE.SESSION_TOKEN);
}

export function invalidateAnonymousJwtCache() {
    anonymousJwtCache = { token: null, fetchedAt: 0 };
}

/** @returns {Promise<string>} raw JWT string */
export async function fetchAuthblocksToken() {
    if (anonymousJwtCache.token && Date.now() - anonymousJwtCache.fetchedAt < ANONYMOUS_CACHE_MS) {
        return anonymousJwtCache.token;
    }
    const base = getAutotraderBaseUrl();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    let res;
    try {
        res = await fetch(`${base}/authblocks`, {
            method : 'POST',
            headers: {
                Accept        : 'application/json',
                'Content-Type': 'application/json',
            },
            signal: controller.signal,
        });
    } finally {
        clearTimeout(timer);
    }
    const text = await res.text();
    let data = {};
    try {
        data = text ? JSON.parse(text) : {};
    } catch (e) {
        throw new AutotraderAuthError(`Invalid JSON from authblocks (${res.status})`, res.status);
    }
    if (!res.ok) {
        throw new AutotraderAuthError(
            (data && (data.message || data.error)) || `authblocks failed (${res.status})`,
            res.status
        );
    }
    const token = parseTokenFromResponse(data);
    anonymousJwtCache = { token, fetchedAt: Date.now() };
    return token;
}

export async function getAutotraderApiToken() {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session && session.access_token) {
            // Keep our custom key in sync with Supabase's auto-refreshed token
            set(AUTOTRADER_STORAGE.SESSION_TOKEN, session.access_token);
            return session.access_token;
        }
    } catch (e) {
        // ignore
    }

    const stored = get(AUTOTRADER_STORAGE.SESSION_TOKEN);
    if (stored && typeof stored === 'string' && isStoredSessionTokenValid(stored)) {
        return stored;
    }
    return fetchAuthblocksToken();
}

export async function initAutotraderSession() {
    try {
        console.log('[initAutotraderSession] checking supabase session...');
        const { data: { session } } = await supabase.auth.getSession();
        console.log('[initAutotraderSession] session:', session ? 'found' : 'null');
        if (!session || !session.access_token) {
            remove(AUTOTRADER_STORAGE.SESSION_TOKEN);
            remove(AUTOTRADER_STORAGE.SITE_LOGGED);
            return;
        }
        set(AUTOTRADER_STORAGE.SESSION_TOKEN, session.access_token);
        set(AUTOTRADER_STORAGE.SITE_LOGGED, '1');
        if (session.user && !get(AUTOTRADER_STORAGE.USER_SNAPSHOT)) {
            const u = session.user;
            set(AUTOTRADER_STORAGE.USER_SNAPSHOT, JSON.stringify(minimalUserSnapshot({
                email   : u.email,
                userId  : u.id,
                fullname: u.user_metadata ? u.user_metadata.full_name || u.user_metadata.name || '' : '',
            })));
        }
    } catch (e) {
        console.error('[initAutotraderSession] error:', e);
    }
}

export async function getAutotraderAuthorizationHeader() {
    const token = await getAutotraderApiToken();
    return { Authorization: `Bearer ${token}` };
}

function minimalUserSnapshot(user) {
    if (!user || typeof user !== 'object') {
        return {};
    }
    const out = {};
    if (user.email) {
        out.email = user.email;
    }
    if (user.userId != null) {
        out.userId = user.userId;
    }
    if (user.fullname != null) {
        out.fullname = user.fullname;
    }
    return out;
}

export async function loginAutotraderSite(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
    });
    if (error) {
        throw new AutotraderAuthError(error.message || 'Login failed');
    }
    const token = data.session ? data.session.access_token : '';
    set(AUTOTRADER_STORAGE.SESSION_TOKEN, token);
    set(AUTOTRADER_STORAGE.SITE_LOGGED, '1');
    if (data.user) {
        const user = {
            email: data.user.email,
            userId: data.user.id,
            fullname: data.user.user_metadata ? data.user.user_metadata.full_name || data.user.user_metadata.name : '',
        };
        set(AUTOTRADER_STORAGE.USER_SNAPSHOT, JSON.stringify(minimalUserSnapshot(user)));
    }
    invalidateAnonymousJwtCache();
    return { token, user: data.user };
}

export async function registerAutotraderSite(email, password) {
    const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
    });
    if (error) {
        throw new AutotraderAuthError(error.message || 'Registration failed');
    }
    const token = data.session ? data.session.access_token : '';
    if (token) {
        set(AUTOTRADER_STORAGE.SESSION_TOKEN, token);
        set(AUTOTRADER_STORAGE.SITE_LOGGED, '1');
    }
    if (data.user) {
        const user = {
            email: data.user.email,
            userId: data.user.id,
            fullname: data.user.user_metadata ? data.user.user_metadata.full_name || data.user.user_metadata.name : '',
        };
        set(AUTOTRADER_STORAGE.USER_SNAPSHOT, JSON.stringify(minimalUserSnapshot(user)));
    }
    invalidateAnonymousJwtCache();
    return { token, user: data.user };
}

export async function clearAutotraderSession() {
    remove(AUTOTRADER_STORAGE.SESSION_TOKEN);
    remove(AUTOTRADER_STORAGE.USER_SNAPSHOT);
    remove(AUTOTRADER_STORAGE.SITE_LOGGED);
    invalidateAnonymousJwtCache();
    try {
        await supabase.auth.signOut();
    } catch (e) {
        // ignore
    }
}

export function isAutotraderUnauthorizedStatus(status) {
    return status === 401 || status === 403;
}

/**
 * After 401/403 from autotrader APIs: clear site session and anonymous JWT cache (see `clearAutotraderSession`).
 */
export function resetAutotraderAuthAfterUnauthorized() {
    clearAutotraderSession();
}

/**
 * GET/POST/etc. to autotrader host. `path` must start with `/` (e.g. `/api/traderbotsplayers/...`).
 *
 * Intentional call sites that do not use this wrapper (Bearer still from `getAutotraderApiToken` /
 * `getAutotraderAuthorizationHeader`):
 * - `View.js`: expert GET (encoded path), `addClient` PUT (XMLHttpRequest)
 * - `blockly/index.js` / `blockly/utils.js`: GET `/traderbots/token` (XHR), premium GET `traderbotsclients` (fetch)
 */
export async function autotraderFetch(path, init = {}) {
    const base = getAutotraderBaseUrl();
    const url = path.startsWith('http') ? path : `${base}${path.startsWith('/') ? path : `/${path}`}`;
    const authHeaders = await getAutotraderAuthorizationHeader();
    return fetch(url, {
        ...init,
        headers: {
            Accept: 'application/json',
            ...(init.headers || {}),
            ...authHeaders,
        },
    });
}
